/**
 * Sticky buy bar on the product page.
 *
 * It owns no cart logic on purpose: the bar's button clicks the product form's
 * real add-to-cart button, so the drawer, sold-out states and error handling stay
 * exactly as the theme defines them.
 *
 * Changing the size in the bar clicks the matching variant swatch rather than
 * ticking it in code. A programmatic `checked = true` plus a synthetic event does
 * not drive the theme — it updates nothing but our own listeners, and the form
 * would still submit the previous variant. A real click goes through the theme's
 * own handling, which is what updates the hidden variant input and the price.
 * Before submitting, the bar checks that input anyway and corrects it if the
 * theme has not caught up.
 *
 * Flow renders the product form twice, for desktop and for mobile, and hides the
 * one that does not apply. Only the visible form is watched and clicked, and the
 * pair is re-resolved when the breakpoint flips.
 */
class StickyAtc extends HTMLElement {
  #resizeTimer = 0;
  #frame = 0;

  connectedCallback() {
    this.select = this.querySelector('[data-sticky-atc-select]');
    this.button = this.querySelector('[data-sticky-atc-button]');
    this.priceHolder = this.querySelector('[data-sticky-atc-price]');

    this.button?.addEventListener('click', this.#addToCart);
    this.select?.addEventListener('change', this.#pickVariant);
    document.addEventListener('change', this.#syncFromForm);
    window.addEventListener('resize', this.#onResize);
    window.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#watchVisibleForm();
    // The form can still be laying out when this runs; one retry after load costs
    // nothing and saves the bar from staying hidden for the whole session.
    window.addEventListener('load', () => this.#watchVisibleForm(), { once: true });
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#syncFromForm);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('scroll', this.#onScroll);
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#resizeTimer);
    document.body.classList.remove('has-sticky-atc');
  }

  /** The add-to-cart button of the form the shopper can actually see.
   *  Never cached: the theme marks that button data-replace-content and swaps the
   *  element on variant change, so a stored reference goes stale and clicking it
   *  does nothing at all. Looked up fresh on every use. */
  #findMainButton() {
    for (const button of document.querySelectorAll('button[name="add"]')) {
      const owner = button.closest('product-form');
      // Both buttons carry the same id attribute; what tells them apart is the
      // section their form belongs to, and one of the two backs quick view.
      const section = owner?.dataset.sectionId ?? '';
      if (section.includes('quick-view')) continue;
      if (button.getBoundingClientRect().height === 0) continue;
      return button;
    }
    return null;
  }

  #watchVisibleForm() {
    this.#update();
  }

  /** Visibility is decided from the button's own position on every frame we care
   *  about. IntersectionObserver was doing the same job with more moving parts —
   *  and one silent failure mode: no anchor found at start-up meant the bar stayed
   *  hidden for the rest of the session. Here a button that appears late simply
   *  starts working on the next scroll. */
  #update = () => {
    const button = this.#findMainButton();
    if (!button) {
      this.#setVisible(false);
      return;
    }

    // Show it once the real button has scrolled off the top of the screen.
    this.#setVisible(button.getBoundingClientRect().bottom < 0);
  };

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #setVisible(visible) {
    this.hidden = !visible;
    // The floating WhatsApp button lifts out of the way while the bar is up. The
    // lift is measured, not guessed: the bar is one row on a wide screen and two on
    // a narrow one, and a fixed offset left the widget sitting under it.
    document.body.classList.toggle('has-sticky-atc', visible);
    if (visible) {
      document.documentElement.style.setProperty('--sticky-atc-lift', `${this.offsetHeight + 12}px`);
    }
  }

  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.#watchVisibleForm(), 150);
  };

  #addToCart = () => {
    const button = this.#findMainButton();
    if (!button || button.disabled) return;

    // Last-ditch guard: submit whatever the bar is showing, even if the theme has
    // not finished reacting to the swatch click.
    const form = button.form;
    const variantInput = form?.querySelector('input[name="id"]');
    if (variantInput && this.select && variantInput.value !== this.select.value) {
      variantInput.value = this.select.value;
    }

    // requestSubmit fires the form's submit event the way a real click does, which
    // is what the theme's product-form element listens for.
    if (form?.requestSubmit) {
      form.requestSubmit(button);
      return;
    }

    button.click();
  };

  /** Bar selector changed: click the matching swatch so the theme does the rest.
   *  The theme re-renders the product form on that click, and the page jumps back
   *  to the top — which also made the bar vanish, since the real button was on
   *  screen again. The scroll position is captured and restored: once on the next
   *  frame, and once more after the re-render has had time to land. */
  #pickVariant = () => {
    this.#paintPrice();

    const holder = document.querySelector(`[data-swatch-variant-id="${this.select.value}"]`);
    const radio = holder?.querySelector('input[data-product-variant]');
    if (!radio || radio.checked) return;

    const top = window.scrollY;
    radio.click();
    this.#keepScroll(top);
  };

  /** Hold the scroll position while the theme re-renders.
   *  Restoring at a few fixed moments was not enough — the re-render lands whenever
   *  it lands, and one late jump was all it took. So the position is held on every
   *  frame for a short window, which also keeps the bar from blinking out: its own
   *  visibility check runs in the same loop. */
  #keepScroll(top) {
    const until = performance.now() + 700;

    const hold = (now) => {
      if (Math.abs(window.scrollY - top) > 2) {
        window.scrollTo({ top, behavior: 'instant' });
      }
      this.#update();
      if (now < until) requestAnimationFrame(hold);
    };

    requestAnimationFrame(hold);
  }

  /** Variant changed in the form: keep the bar selector and price in step. */
  #syncFromForm = (event) => {
    if (!this.select) return;

    const radio = event.target?.closest?.('input[data-product-variant]');
    if (!radio) return;

    const id = radio.closest('[data-swatch-variant-id]')?.getAttribute('data-swatch-variant-id');
    if (!id || this.select.value === id) return;

    this.select.value = id;
    this.#paintPrice();
  };

  #paintPrice() {
    if (!this.priceHolder || !this.select) return;

    const option = this.select.selectedOptions[0];
    if (!option) return;

    const was = option.dataset.comparePrice;
    const now = option.dataset.price;
    this.priceHolder.innerHTML = '';

    if (was) {
      const struck = document.createElement('s');
      struck.className = 'sticky-atc__was';
      // Read out, the two prices come as "675 490" with nothing to tell them apart.
      struck.setAttribute('aria-hidden', 'true');
      struck.textContent = was;
      this.priceHolder.append(struck);
    }

    const current = document.createElement('span');
    current.className = 'sticky-atc__now';
    current.textContent = now;
    this.priceHolder.append(current);
  }
}

customElements.define('sticky-atc', StickyAtc);
